import { FASE_WORLDS, FASES, faseLevel, type FaseTrack } from '../audio/beats/fases';
import { createSong, levelSpec, type LevelSpec, type Song } from '../core/chart';
import { UNLOCK_KEY, boardSeed, type KeyValueStore } from '../core/progress';
import { chartFromTrack, type TrackCalendar, type TrackSong } from '../core/track-chart';
import { DEFAULT_LOOK, isLookId, slugOf, type LookId } from '../render/look';

export interface World {
  readonly n: number;
  readonly name: string;
  readonly look: LookId;
  readonly fases: readonly FaseTrack[];
}

export const FASE_KEY = 'solta.fase.liberada';

export function lookOfWorld(name: string): LookId {
  const slug = slugOf(name);
  return isLookId(slug) ? slug : DEFAULT_LOOK;
}

export const WORLDS: readonly World[] = FASE_WORLDS.map((w) => ({
  n: w.n,
  name: w.name,
  look: lookOfWorld(w.name),
  fases: FASES.filter((f) => f.world === w.n),
}));

export function faseLook(f: FaseTrack): LookId {
  return lookOfWorld(f.worldName);
}

export const FIRST_FASE: FaseTrack = FASES[0] as FaseTrack;

export function worldOf(n: number): World {
  return WORLDS.find((w) => w.n === n) ?? (WORLDS[0] as World);
}

export function faseIndex(f: FaseTrack): number {
  return FASES.findIndex((x) => x.id === f.id);
}

export function faseById(id: string | null | undefined): FaseTrack | null {
  return FASES.find((f) => f.id === id) ?? null;
}

export function faseAt(world: number, fase: number): FaseTrack {
  const w = worldOf(Math.floor(world));
  const i = Math.min(w.fases.length, Math.max(1, Number.isFinite(fase) ? Math.floor(fase) : 1)) - 1;
  return w.fases[i] ?? FIRST_FASE;
}

export function nextFase(f: FaseTrack): FaseTrack | null {
  return FASES[faseIndex(f) + 1] ?? null;
}

function read(store: KeyValueStore | null, key: string): string | null {
  if (store === null) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

export function unlockedIndex(store: KeyValueStore | null): number {
  const saved = faseById(read(store, FASE_KEY));
  if (saved !== null) return faseIndex(saved);
  const old = Number.parseInt(read(store, UNLOCK_KEY) ?? '', 10);
  if (!Number.isFinite(old)) return 0;
  return Math.min(worldOf(1).fases.length, Math.max(1, old)) - 1;
}

export function isUnlocked(store: KeyValueStore | null, f: FaseTrack): boolean {
  return faseIndex(f) <= unlockedIndex(store);
}

export function faseCleared(store: KeyValueStore | null, f: FaseTrack): boolean {
  return faseIndex(f) < unlockedIndex(store);
}

export function clearFase(store: KeyValueStore | null, f: FaseTrack): FaseTrack | null {
  const next = nextFase(f);
  const index = Math.max(unlockedIndex(store), next === null ? faseIndex(f) : faseIndex(next));
  const top = FASES[index] as FaseTrack;
  if (store !== null) {
    try {
      store.setItem(FASE_KEY, top.id);
    } catch {
      return next;
    }
  }
  return next;
}

export function forcedFase(search: string): FaseTrack | null {
  const params = new URLSearchParams(search);
  const world = params.get('mundo');
  const fase = params.get('nivel');
  const blank = (raw: string | null): boolean => raw === null || raw.trim() === '';
  if (blank(world) && blank(fase)) return null;
  return faseAt(blank(world) ? 1 : Number(world), blank(fase) ? 1 : Number(fase));
}

export function startFase(search: string, store: KeyValueStore | null): FaseTrack {
  return forcedFase(search) ?? FASES[unlockedIndex(store)] ?? FIRST_FASE;
}

export function faseTitle(f: FaseTrack): string {
  return `${f.worldName} · F${String(f.fase)} ${f.faseName}`;
}

export interface FaseEntry {
  readonly fase: FaseTrack;
  readonly unlocked: boolean;
  readonly current: boolean;
}

export interface WorldEntry {
  readonly world: World;
  readonly unlocked: boolean;
  readonly current: boolean;
  readonly fases: readonly FaseEntry[];
}

export function worldList(store: KeyValueStore | null, current: FaseTrack): readonly WorldEntry[] {
  const top = Math.max(unlockedIndex(store), faseIndex(current));
  return WORLDS.map((world) => ({
    world,
    current: world.n === current.world,
    unlocked: world.fases.some((f) => faseIndex(f) <= top),
    fases: world.fases.map((f) => ({ fase: f, unlocked: faseIndex(f) <= top, current: f.id === current.id })),
  }));
}

export interface FaseChart {
  readonly song: Song;
  readonly spec: LevelSpec;
  readonly timed: TrackSong | null;
}

export function faseChart(seed: string, f: FaseTrack, calendar: TrackCalendar | null): FaseChart {
  const board = boardSeed(seed, f.id);
  if (calendar === null) return { song: createSong(board, f.level), spec: levelSpec(f.level), timed: null };
  const spec = faseLevel(f);
  const timed = chartFromTrack(board, calendar, spec);
  return { song: timed, spec, timed };
}
